import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  getDocs,
  getAggregateFromServer,
  sum,
  count,
  Timestamp
} from 'firebase/firestore';
import { db } from '../config';
import { DonationTransaction } from '../../../types';
import { useState, useEffect } from 'react';

const COLLECTION_NAME = 'donations';

// Fix 1 (monthly report skip-rule + cross-platform parity): a donation with no stored
// `date` must map to an Invalid Date, NOT to "now" — the report's bucketing already skips
// Invalid Date donations (they count toward no month and no total). Falling back to
// `new Date()` would instead silently attribute the donation to whichever month the report
// happens to render in. Also handles `date` stored as a string or number (mirrors mobile's
// `toDate` in mobile/src/lib/firebase/queries/donations.ts) so both platforms derive the
// same month from the same document instead of a Firestore Timestamp-only read silently
// falling through to "now" while mobile's `new Date(value)` parses the real month.
const toDonationDate = (value: any): Date => {
  if (!value) return new Date(NaN);
  if (typeof value?.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  return new Date(value);
};

const toDonation = (d: any): DonationTransaction => {
  const data = d.data();
  return {
    id: d.id,
    linkId: data.linkId || '',
    platform: data.platform,
    caseNumber: String(data.caseNumber ?? ''),
    amount: data.amount || 0,
    date: toDonationDate(data.date),
    donorName: data.donorName || undefined,
    donorPhone: data.donorPhone || undefined,
    txnRef: data.txnRef || undefined,
    paymentMethod: data.paymentMethod || undefined,
    source: data.source || 'manual',
    importKey: data.importKey || '',
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate(),
  };
};

// Fix 4: onError is optional (additive) so existing callers keep compiling, but useDonations
// below always supplies one — a failed subscription must clear `loading` and fall through to
// the report's empty state instead of spinning forever (mirrors mobile's subscribeDonations,
// which already takes an error handler).
export const subscribeDonations = (
  callback: (items: DonationTransaction[]) => void,
  onError?: (error: Error) => void
) => {
  const q = query(collection(db, COLLECTION_NAME), orderBy('date', 'desc'));
  return onSnapshot(
    q,
    (snapshot) => {
      const t0 = performance.now();
      const items = snapshot.docs.map(toDonation);
      console.log(`[perf] subscribeDonations: mapped ${items.length} docs in ${(performance.now() - t0).toFixed(1)}ms (fromCache=${snapshot.metadata.fromCache})`);
      callback(items);
    },
    (error) => {
      console.error('[donations] subscribeDonations error:', error);
      onError?.(error);
    }
  );
};

/**
 * Server-side per-link totals — replaces downloading the whole `donations` collection just to
 * sum it client-side (subscribeDonations is an unbounded onSnapshot over 74k+ docs). Each call
 * is `sum('amount')` filtered by a single `where('linkId','==',id)` — one field, so no
 * composite index (Firestore auto-indexes every field for equality). Batched with capped
 * concurrency so 176+ links don't fire 176 requests at once; a single link's aggregation
 * failing is logged and treated as 0 rather than failing the whole map (matches
 * lib/firebase/queries/organizations/statistics.ts's per-batch fail-soft convention).
 */
export const getDonationSumsByLinkIds = async (
  linkIds: string[],
  concurrency = 15,
): Promise<Record<string, number>> => {
  const sums: Record<string, number> = {};
  for (let i = 0; i < linkIds.length; i += concurrency) {
    const batch = linkIds.slice(i, i + concurrency);
    await Promise.all(
      batch.map(async (linkId) => {
        try {
          const snap = await getAggregateFromServer(
            query(collection(db, COLLECTION_NAME), where('linkId', '==', linkId)),
            { total: sum('amount') },
          );
          sums[linkId] = snap.data().total || 0;
        } catch (error) {
          console.warn('[donations] getDonationSumsByLinkIds failed for', linkId, error);
          sums[linkId] = 0;
        }
      }),
    );
  }
  return sums;
};

/**
 * Server-side per-month {total, count} for one calendar year — replaces downloading every
 * donation just to bucket it by month client-side (MarketerMonthlyReport). Each of the 12
 * aggregations filters on a single field (`date` range within that month), so no composite
 * index is needed — a range query with two bounds on the SAME field only requires the automatic
 * single-field index. A failed month is logged and treated as {total:0, count:0} rather than
 * failing the whole year. Month boundaries are built in LOCAL time (`new Date(year, month, 1)`)
 * to match linkFinancials.ts's monthlyProjectRows, which keys off
 * `date.getFullYear()`/`getMonth()` — both must agree on which local month a donation belongs to.
 */
export const getMonthlyDonationTotals = async (
  year: number,
): Promise<Record<string, { total: number; count: number }>> => {
  const out: Record<string, { total: number; count: number }> = {};
  const months = Array.from({ length: 12 }, (_, m) => m);
  await Promise.all(
    months.map(async (m) => {
      const key = `${year}-${String(m + 1).padStart(2, '0')}`;
      const start = new Date(year, m, 1);
      const end = new Date(year, m + 1, 1);
      try {
        const snap = await getAggregateFromServer(
          query(
            collection(db, COLLECTION_NAME),
            where('date', '>=', start),
            where('date', '<', end),
          ),
          { total: sum('amount'), count: count() },
        );
        const data = snap.data();
        out[key] = { total: data.total || 0, count: data.count || 0 };
      } catch (error) {
        console.warn('[donations] getMonthlyDonationTotals failed for', key, error);
        out[key] = { total: 0, count: 0 };
      }
    }),
  );
  return out;
};

export interface DonationYearBounds {
  earliest: number | null;
  latest: number | null;
}

/**
 * Earliest and latest calendar years with donation data — two single-document reads
 * (`orderBy('date','asc'|'desc')` + `limit(1)`), NOT a scan of the collection. A single-field
 * orderBy with limit(1) needs no composite index (Firestore auto-indexes every field), so this is
 * 2 document reads total regardless of the collection holding 74k+ docs. Restores the
 * year-navigation bounds that the old client-side scan (subscribeDonations) used to derive for
 * MarketerMonthlyReport, without reintroducing an unbounded read (same fix as mobile's
 * donations.ts getDonationYearBounds). The Firestore Web SDK has no query-level field projection
 * ("select"/field-mask is admin/REST-only), so each read still returns the full document — the
 * win is in row count (1 doc vs 74k+), not payload shaping.
 *
 * Years are derived in LOCAL time via `toDonationDate(...).getFullYear()`, matching
 * getMonthlyDonationTotals' month boundaries and linkFinancials.ts's monthlyProjectRows — both
 * key off local getFullYear()/getMonth(), so the bounds returned here must bucket a given
 * donation into the same year the report itself would. Using `toISOString()` instead reads the
 * UTC year, which can disagree with the local year right around midnight (e.g. a 31 December
 * donation stored just before local midnight could read back as 1 January UTC and roll into the
 * following year).
 *
 * Returns {earliest: null, latest: null} when the collection is empty, a document's `date` is
 * missing/unparseable, or a read fails — callers must fall back to the current year with both
 * nav arrows disabled rather than crash or offer an unbounded range.
 */
export const getDonationYearBounds = async (): Promise<DonationYearBounds> => {
  try {
    const [earliestSnap, latestSnap] = await Promise.all([
      getDocs(query(collection(db, COLLECTION_NAME), orderBy('date', 'asc'), limit(1))),
      getDocs(query(collection(db, COLLECTION_NAME), orderBy('date', 'desc'), limit(1))),
    ]);
    const earliestDoc = earliestSnap.docs[0];
    const latestDoc = latestSnap.docs[0];
    if (!earliestDoc || !latestDoc) return { earliest: null, latest: null };
    const earliestDate = toDonationDate(earliestDoc.data().date);
    const latestDate = toDonationDate(latestDoc.data().date);
    if (Number.isNaN(earliestDate.getTime()) || Number.isNaN(latestDate.getTime())) {
      return { earliest: null, latest: null };
    }
    return { earliest: earliestDate.getFullYear(), latest: latestDate.getFullYear() };
  } catch (error) {
    console.warn('[donations] getDonationYearBounds failed', error);
    return { earliest: null, latest: null };
  }
};

/**
 * A single link's donation rows — fetched on demand (when its edit dialog or manage-donations
 * dialog opens), never up front. Single-field equality filter (`linkId`), so no composite
 * index. Sorted date-desc client-side (matches subscribeDonations' `orderBy('date','desc')`)
 * since adding a Firestore orderBy on a different field than the equality filter would require
 * one.
 */
export const getLinkDonations = async (linkId: string): Promise<DonationTransaction[]> => {
  const snap = await getDocs(query(collection(db, COLLECTION_NAME), where('linkId', '==', linkId)));
  return snap.docs.map(toDonation).sort((a, b) => b.date.getTime() - a.date.getTime());
};

export const addDonation = async (
  entry: Omit<DonationTransaction, 'id' | 'createdAt' | 'updatedAt'>
): Promise<string> => {
  const data: any = {
    ...entry,
    date: entry.date instanceof Date ? Timestamp.fromDate(entry.date) : entry.date,
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  };
  Object.keys(data).forEach(k => { if (data[k] === undefined) delete data[k]; });
  const docRef = await addDoc(collection(db, COLLECTION_NAME), data);
  return docRef.id;
};

export const updateDonation = async (
  id: string,
  updates: Partial<Omit<DonationTransaction, 'id' | 'createdAt'>>
): Promise<void> => {
  const data: any = { ...updates, updatedAt: Timestamp.now() };
  if (data.date instanceof Date) data.date = Timestamp.fromDate(data.date);
  Object.keys(data).forEach(k => { if (data[k] === undefined) delete data[k]; });
  await updateDoc(doc(db, COLLECTION_NAME, id), data);
};

export const deleteDonation = async (id: string): Promise<void> => {
  await deleteDoc(doc(db, COLLECTION_NAME, id));
};

export const useDonations = () => {
  const [donations, setDonations] = useState<DonationTransaction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = subscribeDonations(
      (items) => {
        setDonations(items);
        setLoading(false);
      },
      () => {
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, []);

  return { donations, loading };
};
